"use client";

import React, { useCallback } from "react";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { OfferingLineItemsDisplay } from "./OfferingLineItemsDisplay";
import { OfferingLineItemsEditor } from "./OfferingLineItemsEditor";
import type { StaffLineItem, HardCostLineItem } from "@/lib/types/database/offerings";
import type { LineItemsVariant } from "./OfferingLineItemsEditor";

interface OfferingLineItemsCellProps {
  value: StaffLineItem[] | HardCostLineItem[] | null | undefined;
  fieldId: string;
  entityId: string;
  entityName: string;
  variant: LineItemsVariant;
  onSave: (newValue: StaffLineItem[] | HardCostLineItem[]) => Promise<void>;
  maxDisplayItems?: number;
}

/**
 * Cell component for staff or hard-cost line items in offerings table
 * Displays summary and opens modal editor on click
 */
export function OfferingLineItemsCell({
  value,
  fieldId,
  entityId,
  entityName,
  variant,
  onSave,
  maxDisplayItems = 2,
}: OfferingLineItemsCellProps) {
  const globalModalId = `line-items-editor-${entityName}-${entityId}-${fieldId}`;

  const handleSave = useCallback(
    async (newValue: StaffLineItem[] | HardCostLineItem[]) => {
      try {
        await onSave(newValue);
      } catch (error) {
        console.error("Error saving line items:", error);
        throw error;
      }
    },
    [onSave]
  );

  return (
    <div className="min-w-0 flex items-center gap-2">
      <TooltipWithContent content="Click to view and edit line items">
        <div className="flex-1 cursor-pointer hover:opacity-80 transition-opacity">
          <OfferingLineItemsDisplay
            value={value}
            variant={variant}
            maxItems={maxDisplayItems}
          />
        </div>
      </TooltipWithContent>
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <OfferingLineItemsEditor
          value={value}
          fieldId={fieldId}
          variant={variant}
          onSave={handleSave}
          globalModalId={globalModalId}
        />
      </div>
    </div>
  );
}
